import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ressourcesAPI, partageAPI, partageRessourceAPI } from '../api/api';

function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.focus();
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
  return Promise.resolve();
}

const DOMAINES_PREDEFINIS = [
  'Assurance', 'banque', 'conformité', 'courtage', 'courtage en assurances',
  'courtage en prêts immobilier', "création et gestion d'entreprise", 'crédits immo',
  'digital', 'épargne', 'fiscalité', 'immobilier ancien', 'immobilier neuf',
  'immobilier sens large', 'jurisprudence et textes de lois', 'pédagogie',
  'Prévoyance', 'prospection', 'santé', 'Solidarité', 'vente et relation client',
];

const ACTIONS_PREDEFINIS = [
  'Exploitation supports de formation',
  'Idée posts',
  'Info expertises formateurs',
];

const DOMAINE_COLORS = {
  'Assurance':                        'bg-purple-100 text-purple-700',
  'banque':                           'bg-blue-100 text-blue-700',
  'conformité':                       'bg-red-100 text-red-700',
  'courtage':                         'bg-orange-100 text-orange-700',
  'courtage en assurances':           'bg-orange-100 text-orange-700',
  'courtage en prêts immobilier':     'bg-orange-100 text-orange-700',
  "création et gestion d'entreprise": 'bg-teal-100 text-teal-700',
  'crédits immo':                     'bg-cyan-100 text-cyan-700',
  'digital':                          'bg-indigo-100 text-indigo-700',
  'épargne':                          'bg-green-100 text-green-700',
  'fiscalité':                        'bg-yellow-100 text-yellow-700',
  'immobilier ancien':                'bg-stone-100 text-stone-700',
  'immobilier neuf':                  'bg-emerald-100 text-emerald-700',
  'immobilier sens large':            'bg-lime-100 text-lime-700',
  'jurisprudence et textes de lois':  'bg-rose-100 text-rose-700',
  'pédagogie':                        'bg-sky-100 text-sky-700',
  'Prévoyance':                       'bg-violet-100 text-violet-700',
  'prospection':                      'bg-pink-100 text-pink-700',
  'santé':                            'bg-green-100 text-green-700',
  'Solidarité':                       'bg-fuchsia-100 text-fuchsia-700',
  'vente et relation client':         'bg-amber-100 text-amber-700',
};

const ACTION_COLORS = {
  'Exploitation supports de formation': 'bg-pink-100 text-pink-700',
  'Idée posts':                         'bg-orange-100 text-orange-700',
  'Info expertises formateurs':         'bg-purple-100 text-purple-700',
};

const PASTEL_POOL = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700',
  'bg-yellow-100 text-yellow-700', 'bg-pink-100 text-pink-700',
  'bg-indigo-100 text-indigo-700', 'bg-teal-100 text-teal-700',
];

const getColor = (opt, map) => map[opt] || PASTEL_POOL[opt.charCodeAt(0) % PASTEL_POOL.length];

const fmtDate = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

// ── Dropdown inline Notion ────────────────────────────────────────────────────

function CellDropdown({ options, selected, colorMap, onToggle, onCreate, onClose, anchorRect }) {
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const exactMatch = options.some(o => o.toLowerCase() === search.toLowerCase());
  const canCreate = search.trim() !== '' && !exactMatch;

  const style = {
    position: 'fixed',
    top: anchorRect ? anchorRect.bottom + 4 : 100,
    left: anchorRect ? Math.min(anchorRect.left, window.innerWidth - 288) : 100,
    zIndex: 9999,
    width: 272,
  };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div ref={ref} style={style} className="bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
      <div className="p-2 border-b border-gray-100">
        <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher une option…"
          className="w-full text-sm px-2 py-1.5 bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-300"
          onKeyDown={e => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter' && canCreate) { onCreate(search.trim()); setSearch(''); }
          }} />
      </div>
      <div className="p-1.5 max-h-56 overflow-y-auto">
        {filtered.length === 0 && !canCreate && (
          <p className="text-xs text-gray-400 px-2 py-2">Aucun résultat</p>
        )}
        {filtered.map(opt => {
          const isSel = selected.includes(opt);
          return (
            <div key={opt} onMouseDown={e => { e.preventDefault(); onToggle(opt); }}
              className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 select-none">
              <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isSel ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {isSel && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getColor(opt, colorMap)}`}>{opt}</span>
            </div>
          );
        })}
        {canCreate && (
          <div onMouseDown={e => { e.preventDefault(); onCreate(search.trim()); setSearch(''); }}
            className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 select-none">
            <div className="flex-shrink-0 w-4 h-4 rounded border-2 border-dashed border-gray-400 flex items-center justify-center">
              <span className="text-gray-400 text-xs font-bold leading-none">+</span>
            </div>
            <span className="text-xs text-gray-600">Créer</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">{search.trim()}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panneau de lecture latéral ────────────────────────────────────────────────

function DetailPanel({ ressource, onClose, onSave }) {
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState(ressource.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(ressource.id, { ...ressource, notes });
    setSaving(false);
    setEditingNotes(false);
  };

  const displayDate = ressource.date_publication
    ? fmtDate(ressource.date_publication)
    : fmtDate(ressource.created_at);

  return (
    <div className="fixed inset-0 z-40 flex">
      {/* Overlay */}
      <div className="flex-1 bg-black bg-opacity-20" onClick={onClose} />

      {/* Panneau */}
      <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            {ressource.est_nouveau && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-600">New !</span>
            )}
            {ressource.url && (
              <a href={ressource.url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                Ouvrir le lien
              </a>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Corps scrollable */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Titre */}
          <h1 className="text-2xl font-bold text-gray-900 leading-snug mb-4">{ressource.nom}</h1>

          {/* Métadonnées */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400 mb-4">
            {displayDate && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                {displayDate}
              </span>
            )}
            {ressource.created_by_email && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                {ressource.created_by_email}
              </span>
            )}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-6">
            {(ressource.domaines || []).map(d => (
              <span key={d} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getColor(d, DOMAINE_COLORS)}`}>{d}</span>
            ))}
            {(ressource.actions_requises || []).map(a => (
              <span key={a} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[a] || 'bg-gray-100 text-gray-600'}`}>{a}</span>
            ))}
          </div>

          {/* Notes */}
          <div className="mt-2">
            {editingNotes ? (
              <div className="space-y-3">
                <textarea
                  autoFocus
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={20}
                  placeholder="Ajoutez vos notes, extraits, résumé…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 leading-relaxed focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
                />
                <div className="flex gap-2">
                  <button onClick={save} disabled={saving}
                    className="px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 transition">
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  <button onClick={() => { setNotes(ressource.notes || ''); setEditingNotes(false); }}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                    Annuler
                  </button>
                </div>
              </div>
            ) : notes ? (
              <div
                onDoubleClick={() => setEditingNotes(true)}
                title="Double-clic pour modifier"
                className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed cursor-text rounded-lg hover:bg-gray-50 px-2 py-1 -mx-2 transition"
              >
                {notes}
              </div>
            ) : (
              <button onClick={() => setEditingNotes(true)}
                className="w-full border-2 border-dashed border-gray-200 rounded-xl py-10 text-sm text-gray-400 hover:border-gray-300 hover:text-gray-500 transition flex flex-col items-center gap-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Cliquer pour ajouter des notes
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

const emptyForm = {
  id: null, nom: '', url: '', domaines: [], actions_requises: [],
  est_nouveau: false, notes: '', date_publication: '',
};

function RessourcesList() {
  const [ressources, setRessources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState({ search: '', domaine: '', action: '', est_nouveau: '' });
  const [dropdown, setDropdown] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [editingDateId, setEditingDateId] = useState(null);
  const [customDomaines, setCustomDomaines] = useState([]);
  const [showPartage, setShowPartage] = useState(false);
  const [partageToken, setPartageToken] = useState(null);
  const [partageLoading, setPartageLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  // Partage individuel
  const [shareRessourceId, setShareRessourceId] = useState(null);
  const [shareRessourceToken, setShareRessourceToken] = useState(null);
  const [shareRessourceLoading, setShareRessourceLoading] = useState(false);
  const [copiedRessourceLink, setCopiedRessourceLink] = useState(false);

  const allDomaines = [...new Set([...DOMAINES_PREDEFINIS, ...customDomaines])];
  const detailRessource = ressources.find(r => r.id === detailId) || null;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ressourcesAPI.getAll(filters);
      setRessources(Array.isArray(data) ? data : []);
    } catch { setError('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    partageAPI.getToken().then(t => setPartageToken(t)).catch(() => {});
  }, []);

  const shareUrl = partageToken
    ? `${window.location.origin}/partage/ressources/${partageToken.token}`
    : null;

  const handleGenerateLink = async () => {
    setPartageLoading(true);
    try {
      const t = await partageAPI.createToken();
      setPartageToken(t);
    } finally {
      setPartageLoading(false);
    }
  };

  const handleRevokeLink = async () => {
    if (!window.confirm('Révoquer le lien ? Les formateurs n\'y auront plus accès.')) return;
    await partageAPI.revokeToken();
    setPartageToken(null);
  };

  const handleCopyLink = () => {
    copyToClipboard(shareUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // ── Partage individuel ────────────────────────────────────────────────────

  const openShareRessource = async (r) => {
    setShareRessourceId(r.id);
    setShareRessourceToken(null);
    setShareRessourceLoading(true);
    try {
      const t = await partageRessourceAPI.getToken(r.id);
      setShareRessourceToken(t);
    } finally {
      setShareRessourceLoading(false);
    }
  };

  const handleGenerateRessourceLink = async () => {
    setShareRessourceLoading(true);
    try {
      const t = await partageRessourceAPI.createToken(shareRessourceId);
      setShareRessourceToken(t);
    } finally {
      setShareRessourceLoading(false);
    }
  };

  const handleRevokeRessourceLink = async () => {
    if (!window.confirm('Révoquer ce lien de partage ?')) return;
    await partageRessourceAPI.revokeToken(shareRessourceId);
    setShareRessourceToken(null);
  };

  const shareRessourceUrl = shareRessourceToken
    ? `${window.location.origin}/partage/ressource/${shareRessourceToken.token}`
    : null;

  const handleCopyRessourceLink = () => {
    copyToClipboard(shareRessourceUrl);
    setCopiedRessourceLink(true);
    setTimeout(() => setCopiedRessourceLink(false), 2000);
  };

  // ── Inline dropdown ───────────────────────────────────────────────────────

  const openDropdown = (e, id, field) => {
    e.stopPropagation();
    setDetailId(null);
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdown({ id, field, anchorRect: rect });
  };

  const closeDropdown = useCallback(() => setDropdown(null), []);

  const toggleOption = async (opt) => {
    const r = ressources.find(x => x.id === dropdown.id);
    if (!r) return;
    const field = dropdown.field;
    const current = r[field] || [];
    const next = current.includes(opt) ? current.filter(v => v !== opt) : [...current, opt];
    setRessources(prev => prev.map(x => x.id === r.id ? { ...x, [field]: next } : x));
    try { await ressourcesAPI.update(r.id, { ...r, [field]: next }); }
    catch { load(); }
  };

  const createOption = async (label) => {
    const r = ressources.find(x => x.id === dropdown.id);
    if (!r) return;
    const field = dropdown.field;
    const current = r[field] || [];
    if (current.includes(label)) return;
    const next = [...current, label];
    if (field === 'domaines' && !allDomaines.includes(label)) setCustomDomaines(prev => [...prev, label]);
    setRessources(prev => prev.map(x => x.id === r.id ? { ...x, [field]: next } : x));
    try { await ressourcesAPI.update(r.id, { ...r, [field]: next }); }
    catch { load(); }
  };

  const toggleNouveauInline = async (r) => {
    const next = !r.est_nouveau;
    setRessources(prev => prev.map(x => x.id === r.id ? { ...x, est_nouveau: next } : x));
    try { await ressourcesAPI.update(r.id, { ...r, est_nouveau: next }); }
    catch { load(); }
  };

  const saveContentInline = async (id, data) => {
    await ressourcesAPI.update(id, data);
    setRessources(prev => prev.map(x => x.id === id ? { ...x, ...data } : x));
  };

  const saveDateInline = async (r, dateStr) => {
    const date_publication = dateStr || null;
    setRessources(prev => prev.map(x => x.id === r.id ? { ...x, date_publication } : x));
    setEditingDateId(null);
    try { await ressourcesAPI.update(r.id, { ...r, date_publication }); }
    catch { load(); }
  };

  // ── Modal ─────────────────────────────────────────────────────────────────

  const openCreate = () => { setForm(emptyForm); setIsEditing(false); setShowModal(true); };
  const openEdit = (r) => {
    setForm({
      id: r.id, nom: r.nom, url: r.url || '', domaines: r.domaines || [],
      actions_requises: r.actions_requises || [], est_nouveau: r.est_nouveau || false,
      notes: r.notes || '',
      date_publication: r.date_publication ? r.date_publication.substring(0, 10) : '',
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette ressource ?')) return;
    await ressourcesAPI.remove(id);
    if (detailId === id) setDetailId(null);
    load();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) await ressourcesAPI.update(form.id, form);
    else await ressourcesAPI.create(form);
    setShowModal(false);
    load();
  };

  const toggleMulti = (field, value) => setForm(prev => ({
    ...prev,
    [field]: prev[field].includes(value) ? prev[field].filter(v => v !== value) : [...prev[field], value],
  }));

  const setFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const clearFilters = () => setFilters({ search: '', domaine: '', action: '', est_nouveau: '' });
  const hasFilters = Object.values(filters).some(v => v !== '');

  const dropdownData = dropdown ? {
    r: ressources.find(x => x.id === dropdown.id),
    options: dropdown.field === 'domaines' ? allDomaines : ACTIONS_PREDEFINIS,
    colorMap: dropdown.field === 'domaines' ? DOMAINE_COLORS : ACTION_COLORS,
  } : null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 uppercase mb-2">
          Ressources documentaires
        </h1>
        <p className="text-gray-500 text-sm">
          Bienvenue dans cet espace de partage de ressources d'AXELERANCE Formation &amp; Conseil.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm flex justify-between">
          {error}<button onClick={() => setError(null)} className="underline ml-4">Fermer</button>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 items-center mb-6">
        <input type="text" placeholder="🔍  Rechercher…" value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
          className="border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 w-48" />
        <select value={filters.domaine} onChange={e => setFilter('domaine', e.target.value)}
          className="border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300">
          <option value="">Tous les domaines</option>
          {allDomaines.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filters.action} onChange={e => setFilter('action', e.target.value)}
          className="border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300">
          <option value="">Toutes les actions</option>
          {ACTIONS_PREDEFINIS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filters.est_nouveau} onChange={e => setFilter('est_nouveau', e.target.value)}
          className="border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300">
          <option value="">Toutes</option>
          <option value="true">Nouvelles seulement</option>
        </select>
        {hasFilters && <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-gray-600 underline">Réinitialiser</button>}
        <div className="flex-1" />
        <button onClick={() => setShowPartage(true)}
          className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded text-sm font-medium transition">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
          Partager
        </button>
        <button onClick={openCreate}
          className="flex items-center gap-1.5 bg-gray-900 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm font-medium transition">
          + Ajouter
        </button>
      </div>

      <div className="mb-2">
        <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">Domaines.</span>
      </div>

      {/* Table */}
      <div className="w-full" onClick={() => dropdown && closeDropdown()}>
        <div className="hidden md:grid grid-cols-[72px_180px_1fr_90px_260px_52px] gap-2 px-3 py-2 border-b border-gray-200">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">NEW !</span>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Actions requises</span>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Nom</span>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Date</span>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Domaines</span>
          <span />
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-gray-400" />
          </div>
        ) : ressources.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            Aucune ressource{hasFilters ? ' pour ces filtres' : ''}.
          </div>
        ) : ressources.map(r => (
          <div key={r.id}
            className={`group md:grid md:grid-cols-[72px_180px_1fr_90px_260px_52px] gap-2 px-3 py-2.5 border-b border-gray-100 items-start transition
              ${detailId === r.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>

            {/* NEW! */}
            <div className="flex items-center mb-1 md:mb-0">
              <button onClick={() => toggleNouveauInline(r)} title={r.est_nouveau ? 'Retirer New !' : 'Marquer New !'} className="focus:outline-none">
                {r.est_nouveau
                  ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-600">New !</span>
                  : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-gray-300 border border-dashed border-gray-200 opacity-0 group-hover:opacity-100 transition">+ New</span>
                }
              </button>
            </div>

            {/* Actions */}
            <div onClick={e => { e.stopPropagation(); openDropdown(e, r.id, 'actions_requises'); }}
              className={`flex flex-wrap gap-1 mb-1 md:mb-0 min-h-[24px] rounded px-1 py-0.5 cursor-pointer transition
                ${dropdown?.id === r.id && dropdown?.field === 'actions_requises' ? 'ring-2 ring-blue-300 bg-blue-50' : 'hover:bg-gray-100'}`}>
              {(r.actions_requises || []).length === 0 &&
                <span className="text-xs text-gray-300 opacity-0 group-hover:opacity-100 transition">Éditer</span>}
              {(r.actions_requises || []).map(a => (
                <span key={a} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[a] || 'bg-gray-100 text-gray-600'}`}>{a}</span>
              ))}
            </div>

            {/* Nom */}
            <div className="flex items-start gap-2 min-w-0 mb-1 md:mb-0">
              <span className="text-gray-300 mt-0.5 shrink-0 text-sm">{r.notes ? '📝' : '📄'}</span>
              <div className="min-w-0">
                {r.url
                  ? <a href={r.url} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-gray-800 hover:text-blue-600 hover:underline leading-snug break-words font-medium">{r.nom}</a>
                  : <span className="text-sm text-gray-800 leading-snug break-words font-medium">{r.nom}</span>
                }
                {r.notes && <p className="text-xs text-gray-400 mt-0.5 italic line-clamp-2">{r.notes}</p>}
              </div>
            </div>

            {/* Date — clic pour éditer inline */}
            <div className="mb-1 md:mb-0 mt-0.5">
              {editingDateId === r.id ? (
                <input
                  autoFocus
                  type="date"
                  defaultValue={r.date_publication ? r.date_publication.substring(0, 10) : ''}
                  onBlur={e => saveDateInline(r, e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveDateInline(r, e.target.value);
                    if (e.key === 'Escape') setEditingDateId(null);
                  }}
                  className="text-xs border border-blue-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 w-28"
                />
              ) : (
                <button
                  onClick={e => { e.stopPropagation(); setEditingDateId(r.id); }}
                  className="text-xs text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded px-1 py-0.5 transition text-left w-full"
                  title="Cliquer pour modifier la date"
                >
                  {fmtDate(r.date_publication || r.created_at) || <span className="italic text-gray-300">— date</span>}
                </button>
              )}
            </div>

            {/* Domaines */}
            <div onClick={e => { e.stopPropagation(); openDropdown(e, r.id, 'domaines'); }}
              className={`flex flex-wrap gap-1 items-start min-h-[24px] rounded px-1 py-0.5 cursor-pointer transition
                ${dropdown?.id === r.id && dropdown?.field === 'domaines' ? 'ring-2 ring-blue-300 bg-blue-50' : 'hover:bg-gray-100'}`}>
              {(r.domaines || []).length === 0 &&
                <span className="text-xs text-gray-300 opacity-0 group-hover:opacity-100 transition">Éditer</span>}
              {(r.domaines || []).map(d => (
                <span key={d} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getColor(d, DOMAINE_COLORS)}`}>{d}</span>
              ))}
            </div>

            {/* Actions contextuelles */}
            <div className="flex items-center gap-1 shrink-0 mt-0.5">
              {/* Ouvrir panneau — toujours visible */}
              <button
                onClick={() => setDetailId(detailId === r.id ? null : r.id)}
                title="Ouvrir le détail"
                className={`p-0.5 rounded transition ${detailId === r.id ? 'text-blue-500' : 'text-gray-300 hover:text-blue-500'}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              </button>
              {/* Partager, Modifier, Supprimer — au survol */}
              <button onClick={() => openShareRessource(r)} title="Partager cette ressource" className="text-gray-300 hover:text-green-500 p-0.5 rounded opacity-0 group-hover:opacity-100 transition">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
              </button>
              <button onClick={() => openEdit(r)} title="Modifier" className="text-gray-300 hover:text-blue-500 p-0.5 rounded opacity-0 group-hover:opacity-100 transition">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
              <button onClick={() => handleDelete(r.id)} title="Supprimer" className="text-gray-300 hover:text-red-500 p-0.5 rounded opacity-0 group-hover:opacity-100 transition">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 text-xs text-gray-400">{ressources.length} ressource{ressources.length !== 1 ? 's' : ''}</div>

      {/* Dropdown inline */}
      {dropdown && dropdownData?.r && (
        <CellDropdown
          options={dropdownData.options}
          selected={dropdownData.r[dropdown.field] || []}
          colorMap={dropdownData.colorMap}
          onToggle={toggleOption}
          onCreate={createOption}
          onClose={closeDropdown}
          anchorRect={dropdown.anchorRect}
        />
      )}

      {/* Modal partage individuel */}
      {shareRessourceId && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                Partager cette ressource
              </h3>
              <button onClick={() => { setShareRessourceId(null); setShareRessourceToken(null); }} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-500">
                Générez un lien unique pour partager <strong>uniquement cette ressource</strong> en lecture seule, sans compte requis.
              </p>
              {shareRessourceLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400" />
                </div>
              ) : shareRessourceUrl ? (
                <>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-600 flex-1 truncate font-mono">{shareRessourceUrl}</span>
                    <button onClick={handleCopyRessourceLink}
                      className={`shrink-0 text-xs font-medium px-2 py-1 rounded transition ${copiedRessourceLink ? 'bg-green-100 text-green-700' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                      {copiedRessourceLink ? '✓ Copié !' : 'Copier'}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    Lien actif — une seule ressource visible
                  </div>
                  <div className="flex gap-2 pt-2 border-t">
                    <button onClick={handleGenerateRessourceLink} disabled={shareRessourceLoading}
                      className="flex-1 text-sm border border-gray-200 rounded-lg py-2 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50">
                      Regénérer
                    </button>
                    <button onClick={handleRevokeRessourceLink}
                      className="text-sm border border-red-200 text-red-600 rounded-lg px-3 py-2 hover:bg-red-50 transition">
                      Révoquer
                    </button>
                  </div>
                </>
              ) : (
                <button onClick={handleGenerateRessourceLink} disabled={shareRessourceLoading}
                  className="w-full bg-gray-900 hover:bg-gray-700 text-white rounded-lg py-2.5 text-sm font-medium transition disabled:opacity-50 flex items-center justify-center gap-2">
                  {shareRessourceLoading
                    ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Génération…</>
                    : 'Générer le lien de partage'
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de partage */}
      {showPartage && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                Partager les ressources
              </h3>
              <button onClick={() => setShowPartage(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-500">
                Générez un lien unique pour permettre aux formateurs externes de consulter les ressources <strong>en lecture seule</strong>, sans compte requis.
              </p>

              {shareUrl ? (
                <>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-600 flex-1 truncate font-mono">{shareUrl}</span>
                    <button onClick={handleCopyLink}
                      className={`shrink-0 text-xs font-medium px-2 py-1 rounded transition ${copiedLink ? 'bg-green-100 text-green-700' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                      {copiedLink ? '✓ Copié !' : 'Copier'}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    Lien actif — toute personne avec ce lien peut voir les ressources
                  </div>
                  <div className="flex gap-2 pt-2 border-t">
                    <button onClick={handleGenerateLink} disabled={partageLoading}
                      className="flex-1 text-sm border border-gray-200 rounded-lg py-2 text-gray-600 hover:bg-gray-50 transition disabled:opacity-50">
                      Regénérer un nouveau lien
                    </button>
                    <button onClick={handleRevokeLink}
                      className="text-sm border border-red-200 text-red-600 rounded-lg px-3 py-2 hover:bg-red-50 transition">
                      Révoquer
                    </button>
                  </div>
                </>
              ) : (
                <button onClick={handleGenerateLink} disabled={partageLoading}
                  className="w-full bg-gray-900 hover:bg-gray-700 text-white rounded-lg py-2.5 text-sm font-medium transition disabled:opacity-50 flex items-center justify-center gap-2">
                  {partageLoading
                    ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Génération…</>
                    : 'Générer le lien de partage'
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Panneau de lecture */}
      {detailRessource && (
        <DetailPanel
          ressource={detailRessource}
          onClose={() => setDetailId(null)}
          onSave={saveContentInline}
        />
      )}

      {/* Modal ajout / édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-gray-900">{isEditing ? 'Modifier' : 'Nouvelle ressource'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Titre *</label>
                <input type="text" required value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
                  placeholder="Titre de la ressource"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">URL</label>
                <input type="url" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
                  placeholder="https://…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date de publication</label>
                <input type="date" value={form.date_publication} onChange={e => setForm(p => ({ ...p, date_publication: e.target.value }))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Domaines</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {allDomaines.map(d => (
                    <label key={d} className="flex items-center gap-2 cursor-pointer py-0.5">
                      <input type="checkbox" checked={form.domaines.includes(d)} onChange={() => toggleMulti('domaines', d)} className="rounded text-blue-600" />
                      <span className="text-xs text-gray-700">{d}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Actions requises</label>
                <div className="flex flex-col gap-1.5">
                  {ACTIONS_PREDEFINIS.map(a => (
                    <label key={a} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.actions_requises.includes(a)} onChange={() => toggleMulti('actions_requises', a)} className="rounded text-blue-600" />
                      <span className="text-sm text-gray-700">{a}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Notes libres…" rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300" />
              </div>
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.est_nouveau} onChange={e => setForm(p => ({ ...p, est_nouveau: e.target.checked }))} className="rounded w-4 h-4" />
                  <span className="text-sm text-gray-700 font-medium">Marquer comme <span className="text-red-500 font-bold">New !</span></span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Annuler</button>
                <button type="submit"
                  className="px-4 py-2 text-sm bg-gray-900 hover:bg-gray-700 text-white rounded-lg font-medium transition">
                  {isEditing ? 'Enregistrer' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RessourcesList;
